import 'katex/dist/katex.min.css';
import classes from './markdown.module.css';

import clsx from 'clsx';
import { useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import { bracketedMathPlugin } from './plugins/bracketed-math-plugin.js';
import {
	GetMarkdownPluginSanitizeAttributes,
	GetMarkdownPluginsObject,
	RemarkMarkdownPlugins,
} from './plugins/markdown-plugins.js';
import { unknownDirectiveFallbackPlugin } from './plugins/unknown-directive-fallback-plugin.js';

import type { ComponentPropsWithoutRef } from 'react';
import type { Components, ExtraProps, Options as ReactMarkdownProps } from 'react-markdown';

export type MarkdownTableLayout = 'scroll' | 'wrap' | 'wrap-prose';

export type MarkdownProps = Omit<ReactMarkdownProps, 'remarkPlugins' | 'rehypePlugins'> & {
	/**
	 * `scroll` (default) — horizontal scroll for wide tables.
	 * `wrap` — instruction modals; narrow first column, wrapping Purpose text.
	 * `wrap-prose` — docs-style tables; full width with wrapping in every cell.
	 */
	tableLayout?: MarkdownTableLayout;
};

const markdownSanitizeSchema = {
	...defaultSchema,
	tagNames: [...(defaultSchema.tagNames ?? []), ...Object.keys(RemarkMarkdownPlugins)],
	attributes: {
		...defaultSchema.attributes,
		...GetMarkdownPluginSanitizeAttributes(),
	},
};

function ScrollableTable({ node: _node, ...props }: ComponentPropsWithoutRef<'table'> & ExtraProps) {
	return (
		<div className='overflow-auto [&>table]:w-max'>
			<table {...props} />
		</div>
	);
}

function WrappingTable({ node: _node, ...props }: ComponentPropsWithoutRef<'table'> & ExtraProps) {
	return (
		<div className={classes.wrappingTable}>
			<table {...props} />
		</div>
	);
}

/**
 * Render markdown paragraphs as a `<div>` rather than `<p>`. Inline directives
 * (`:module[…]`, `:net[…]`) render interactive MUI Chips, whose root element is a
 * `<div>` — invalid (and a React hydration error) inside a `<p>`. The display
 * markdown spaces blocks via the wrapper's `flex gap`, not via `<p>` margins, so
 * this is visually identical. Sanitization runs on the source `<p>` node and is
 * unaffected by this render-time substitution.
 */
function ParagraphBlock({ node: _node, ...props }: ComponentPropsWithoutRef<'p'> & ExtraProps) {
	return <div {...props} />;
}

function WrappingTableProse({ node: _node, ...props }: ComponentPropsWithoutRef<'table'> & ExtraProps) {
	return (
		<div className={classes.wrappingTableProse}>
			<table {...props} />
		</div>
	);
}

/**
 * @param tableLayout - Selected markdown table presentation.
 * @returns React-markdown table component for the layout.
 */
function resolveMarkdownTableComponent(tableLayout: MarkdownTableLayout): Components['table'] {
	switch (tableLayout) {
		case 'wrap':
			return WrappingTable;
		case 'wrap-prose':
			return WrappingTableProse;
		default:
			return ScrollableTable;
	}
}

const defaultMarkdownComponents: Components = {
	table: ScrollableTable,
	p: ParagraphBlock,
	...GetMarkdownPluginsObject(),
};

export function Markdown({ tableLayout = 'scroll', ...props }: MarkdownProps) {
	const components = useMemo(
		(): Components => ({
			...defaultMarkdownComponents,
			table: resolveMarkdownTableComponent(tableLayout),
		}),
		[tableLayout],
	);

	return (
		<div className={clsx(classes.formatMarkdown, 'flex grow flex-col gap-4 dark:text-gray-a11')}>
			<ReactMarkdown
				{...props}
				remarkPlugins={[
					remarkGfm,
					remarkMath,
					bracketedMathPlugin,
					remarkDirective,
					...Object.values(RemarkMarkdownPlugins).map((p) => p.directive),
					unknownDirectiveFallbackPlugin,
				]}
				rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema], rehypeKatex]} // Ensure `rehypeKatex` comes after `rehypeSanitize`, else math will be sanitized away
				remarkRehypeOptions={{ allowDangerousHtml: true }}
				components={components}
			/>
		</div>
	);
}
